import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { patchMaterial } from '../render/materials';
import type { SharedUniforms } from '../world/Environment';
import { clamp, damp, lerp, smoothstep } from '../world/noise';
import { createKasa, createSaya, createSword } from './Accessories';
import { ClothRibbon, type ClothCollider } from './Cloth';
import { buildCollar, buildHakama, buildKimonoTop, buildNeckWrap, buildObi, buildSleeves, type BonePositions } from './Garments';
import { SwordRig } from './SwordRig';

const WALK_CYCLE_METERS = 1.47;
const RUN_CYCLE_METERS = 2.37;
// Foot-contact phases measured from the clips (walk timing); run is shifted to line up.
const CONTACT_L = 0.26;
const CONTACT_R = 0.75;
const RUN_PHASE_OFFSET = 0.06;
/** Front flip on the double jump: one full turn about the hips. */
const FLIP_TIME = 0.55;
const FLIP_CENTER = new THREE.Vector3(0, 0.95, 0);

export type Locomotion = {
  speed: number;
  grounded: boolean;
  verticalVelocity: number;
  turnRate: number;
  sprint: number;
  crouch: boolean;
  slide: boolean;
};

/**
 * Sleeves are rigid tubes on the arm bones; when the hand rises above the shoulder the cloth should
 * fall. The shader turns each ring's lobe to hang under the arm, slides it toward the shoulder and
 * pulls it in, scaled by how high the arm points. Arms at rest are untouched.
 */
const SLEEVE_DRAPE = /* glsl */ `
if (aSleeve.w > 0.001) {
  vec3 sc = (skinMatrix * vec4(aSleeve.xyz, 1.0)).xyz;
  vec3 axis = normalize(mat3(skinMatrix) * vec3(sign(aSleeve.x), 0.0, 0.0));
  float raise = smoothstep(-0.15, 0.7, axis.y) * aSleeve.w;
  if (raise > 0.001) {
    vec3 o = transformed - sc;
    vec3 dn = mat3(skinMatrix) * vec3(0.0, -1.0, 0.0);
    dn = normalize(dn - axis * dot(dn, axis) + vec3(1e-5));
    vec3 g = vec3(0.0, -1.0, 0.0) + axis * axis.y;
    float gl = length(g);
    if (gl > 0.15) {
      g /= gl;
      float ang = atan(dot(cross(dn, g), axis), dot(dn, g)) * raise;
      float c = cos(ang);
      float s = sin(ang);
      o = o * c + cross(axis, o) * s + axis * dot(axis, o) * (1.0 - c);
    }
    o *= 1.0 - 0.4 * raise;
    transformed = sc + o - axis * 0.1 * raise;
  }
}
`;

function drapeSleeves<T extends THREE.Material>(material: T): T {
  const base = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    base.call(material, shader, renderer);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec4 aSleeve;')
      .replace('#include <skinning_vertex>', `#include <skinning_vertex>\n${SLEEVE_DRAPE}`);
  };
  material.customProgramCacheKey = () => 'kaze-sleeve-drape';
  return material;
}

export type FootstepEvent = { foot: 'L' | 'R'; intensity: number };

type RibbonRig = {
  ribbon: ClothRibbon;
  bone: THREE.Bone;
  local: THREE.Vector3;
  hang: THREE.Vector3;
};

type ColliderRig = { bone: THREE.Bone; local: THREE.Vector3; collider: ClothCollider };

const tmpV = new THREE.Vector3();
const tmpQ = new THREE.Quaternion();
const axisX = new THREE.Vector3(1, 0, 0);
const hangDown = new THREE.Vector3();
const sideRef = new THREE.Vector3();
const axisY = new THREE.Vector3(0, 1, 0);
const qLean = new THREE.Quaternion();
const qTurn = new THREE.Quaternion();
const tmpEuler = new THREE.Euler();

export class Character {
  readonly root = new THREE.Group();
  private readonly pivot = new THREE.Group();
  private model!: THREE.Object3D;
  private mixer!: THREE.AnimationMixer;
  private idle!: THREE.AnimationAction;
  private walk!: THREE.AnimationAction;
  private run!: THREE.AnimationAction;
  private walkDuration = 1;
  private runDuration = 1;
  private readonly bones: Record<string, THREE.Bone> = {};
  private kasa!: THREE.Object3D;
  private readonly ribbons: RibbonRig[] = [];
  private readonly colliders: ColliderRig[] = [];
  private phase = 0;
  private airBlend = 0;
  private lean = 0;
  private bank = 0;
  private landDip = 0;
  private landVel = 0;
  private wasGrounded = true;
  private bowTime = -1;
  private idleLook = 0;
  private flipTime = -1;
  private flipAngle = 0;
  private flipTuck = 0;
  private crouchW = 0;
  private slideW = 0;
  sword!: SwordRig;
  readonly footsteps: FootstepEvent[] = [];
  loaded = false;

  constructor(private readonly uniforms: SharedUniforms) {
    this.root.name = 'player-root';
    this.root.add(this.pivot);
  }

  async load(url: string): Promise<void> {
    const gltf = await new GLTFLoader().loadAsync(url);
    this.model = gltf.scene;
    // Garments and attachments are authored in model space, so bind with the rig at the origin.
    this.root.position.set(0, 0, 0);
    this.root.rotation.set(0, 0, 0);
    this.pivot.rotation.set(0, 0, 0);
    this.pivot.add(this.model);
    this.root.updateMatrixWorld(true);
    const skinned: THREE.SkinnedMesh[] = [];
    this.model.traverse((object) => {
      if ((object as THREE.SkinnedMesh).isSkinnedMesh) skinned.push(object as THREE.SkinnedMesh);
      if ((object as THREE.Bone).isBone) this.bones[object.name.replace('mixamorig', '')] = object as THREE.Bone;
    });
    this.model.updateMatrixWorld(true);

    const u = this.uniforms;
    const bodyMat = patchMaterial(
      new THREE.MeshPhysicalMaterial({
        color: 0x141519,
        roughness: 0.86,
        sheen: 0.8,
        sheenRoughness: 0.55,
        sheenColor: new THREE.Color(0x4d463c),
        envMapIntensity: 0.55,
      }),
      u,
      { rim: 0.45 },
    );
    for (const mesh of skinned) {
      mesh.material = bodyMat;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
    }

    const skeleton = skinned[0].skeleton;
    const boneIndex = (name: string) => {
      const index = skeleton.bones.findIndex((b) => b.name === `mixamorig${name}`);
      if (index < 0) throw new Error(`Missing bone ${name}`);
      return index;
    };
    const bind: BonePositions = {};
    for (const [name, bone] of Object.entries(this.bones)) bind[name] = new THREE.Vector3().setFromMatrixPosition(bone.matrixWorld);

    const fabric = (color: number, sheen: number, rim: number, flutter = false) =>
      patchMaterial(
        new THREE.MeshPhysicalMaterial({
          color,
          roughness: 0.9,
          sheen: 1,
          sheenRoughness: 0.5,
          sheenColor: new THREE.Color(sheen),
          side: THREE.DoubleSide,
          envMapIntensity: 0.5,
        }),
        u,
        { rim, flutter },
      );
    const jacketMat = fabric(0x1b1c21, 0x5c574e, 0.55, true);
    const hakamaMat = fabric(0x121318, 0x4a463f, 0.5, true);
    const obiMat = fabric(0x6f5d42, 0x9c8a68, 0.1);
    const scarfMat = fabric(0x3d1512, 0x8a4a3a, 0.55);

    // Xbot's inverse bind matrices already absorb the armature's 0.01 scale: mesh space is meters.
    const bindMatrix = new THREE.Matrix4();
    const addGarment = (geometry: THREE.BufferGeometry, material: THREE.Material) => {
      const mesh = new THREE.SkinnedMesh(geometry, material);
      mesh.bind(skeleton, bindMatrix);
      mesh.frustumCulled = false;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.model.add(mesh);
    };
    addGarment(buildHakama(bind, boneIndex), hakamaMat);
    addGarment(buildKimonoTop(boneIndex), jacketMat);
    addGarment(buildSleeves(bind, boneIndex), drapeSleeves(fabric(0x1b1c21, 0x5c574e, 0.55, true)));
    addGarment(buildObi(boneIndex), obiMat);
    addGarment(buildNeckWrap(bind, boneIndex), scarfMat);
    addGarment(buildCollar(boneIndex), fabric(0x5e584c, 0xa89e88, 0.2));

    this.kasa = createKasa(u);
    this.attachRigid('Head', this.kasa, new THREE.Vector3(0, 1.712, 0.012), new THREE.Euler(0.1, 0, 0));
    // The saya rides on the back, handle over the right shoulder; the sword seats in it or follows the right hand.
    const saya = createSaya(u);
    const sayaRot = new THREE.Quaternion()
      .setFromEuler(new THREE.Euler(0, 0, THREE.MathUtils.degToRad(33)))
      .multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI, 0)));
    this.attachRigid('Spine2', saya, new THREE.Vector3(-0.1, 1.42, -0.2), sayaRot);
    const katana = createSword(u);
    this.root.add(katana.group);

    // Koshi-ita: the stiff back plate of the hakama, hiding the obi knot.
    const plateShape = new THREE.Shape();
    plateShape.moveTo(-0.13, 0);
    plateShape.lineTo(0.13, 0);
    plateShape.lineTo(0.1, 0.13);
    plateShape.lineTo(-0.1, 0.13);
    plateShape.closePath();
    const plateGeo = new THREE.ExtrudeGeometry(plateShape, { depth: 0.02, bevelEnabled: true, bevelThickness: 0.004, bevelSize: 0.004, bevelSegments: 1 });
    const plate = new THREE.Mesh(plateGeo, hakamaMat);
    plate.castShadow = true;
    this.attachRigid('Hips', plate, new THREE.Vector3(0, 1.0, -0.19), new THREE.Euler(-0.12, 0, 0));

    this.addRibbon('Neck', new THREE.Vector3(0.025, 1.53, -0.085), new THREE.Vector3(0.05, -1, -0.35), 0.78, 0.075, scarfMat, 12);
    this.addRibbon('Neck', new THREE.Vector3(-0.03, 1.52, -0.085), new THREE.Vector3(-0.05, -1, -0.3), 0.64, 0.065, scarfMat, 10);
    this.addRibbon('Hips', new THREE.Vector3(0.05, 1.0, -0.2), new THREE.Vector3(0.1, -1, -0.25), 0.36, 0.032, hakamaMat, 7);
    this.addRibbon('Hips', new THREE.Vector3(-0.045, 0.995, -0.2), new THREE.Vector3(-0.1, -1, -0.25), 0.3, 0.03, hakamaMat, 6);

    this.addCollider('Head', new THREE.Vector3(0, 1.62, 0.02), 0.14);
    this.addCollider('Neck', new THREE.Vector3(0, 1.5, 0.02), 0.1);
    this.addCollider('Spine2', new THREE.Vector3(0, 1.36, -0.05), 0.17);
    this.addCollider('Spine1', new THREE.Vector3(0, 1.24, -0.045), 0.165);
    this.addCollider('Hips', new THREE.Vector3(0, 1.02, -0.035), 0.19);
    this.addCollider('LeftUpLeg', new THREE.Vector3(0.1, 0.75, -0.02), 0.15);
    this.addCollider('RightUpLeg', new THREE.Vector3(-0.1, 0.75, -0.02), 0.15);

    this.setupAnimation(gltf.animations);
    this.sword = new SwordRig(this.bones, this.model, this.root, saya, katana.group, katana.blade);
    this.loaded = true;
  }

  /** Parent `object` to a bone so it sits at the given bind-pose transform (model space, meters). */
  private attachRigid(boneName: string, object: THREE.Object3D, position: THREE.Vector3, rotation: THREE.Euler | THREE.Quaternion): void {
    const bone = this.bones[boneName];
    const quat = rotation instanceof THREE.Quaternion ? rotation : new THREE.Quaternion().setFromEuler(rotation);
    const desired = new THREE.Matrix4().compose(position, quat, new THREE.Vector3(1, 1, 1));
    const holder = new THREE.Object3D();
    holder.matrix.multiplyMatrices(bone.matrixWorld.clone().invert(), desired);
    holder.matrix.decompose(holder.position, holder.quaternion, holder.scale);
    holder.add(object);
    bone.add(holder);
  }

  private addRibbon(boneName: string, anchor: THREE.Vector3, hang: THREE.Vector3, length: number, width: number, material: THREE.Material, segments: number): void {
    const bone = this.bones[boneName];
    const local = bone.worldToLocal(anchor.clone());
    const ribbon = new ClothRibbon(segments, length, width, material, 0.55, 3.0, 1);
    this.ribbons.push({ ribbon, bone, local, hang: hang.clone().normalize() });
  }

  private addCollider(boneName: string, center: THREE.Vector3, radius: number): void {
    const bone = this.bones[boneName];
    this.colliders.push({ bone, local: bone.worldToLocal(center.clone()), collider: { center: new THREE.Vector3(), radius } });
  }

  /** Cloth meshes live in world space, so they are added to the scene separately. */
  get clothMeshes(): THREE.Mesh[] {
    return this.ribbons.map((r) => r.ribbon.mesh);
  }

  private setupAnimation(clips: THREE.AnimationClip[]): void {
    this.mixer = new THREE.AnimationMixer(this.model);
    const find = (name: string) => {
      const clip = clips.find((c) => c.name === name);
      if (!clip) throw new Error(`Missing clip ${name}`);
      return clip;
    };
    const idleClip = find('idle');
    const walkClip = find('walk');
    const runClip = find('run');
    // Center hip translation so crossfades between clips never slide the body.
    for (const clip of [idleClip, walkClip, runClip]) {
      const track = clip.tracks.find((t) => t.name.endsWith('Hips.position'));
      if (!track) continue;
      const values = track.values;
      const n = values.length / 3;
      let mx = 0;
      let mz = 0;
      for (let i = 0; i < n; i++) {
        mx += values[i * 3];
        mz += values[i * 3 + 2];
      }
      mx /= n;
      mz /= n;
      for (let i = 0; i < n; i++) {
        values[i * 3] -= mx;
        values[i * 3 + 2] -= mz - 1.8;
      }
    }
    this.idle = this.mixer.clipAction(idleClip);
    this.walk = this.mixer.clipAction(walkClip);
    this.run = this.mixer.clipAction(runClip);
    this.walkDuration = walkClip.duration;
    this.runDuration = runClip.duration;
    for (const action of [this.idle, this.walk, this.run]) {
      action.play();
      action.setEffectiveWeight(0);
    }
    this.walk.timeScale = 0;
    this.run.timeScale = 0;
    this.idle.setEffectiveWeight(1);
  }

  bow(): void {
    this.bowTime = 0;
  }

  get bowing(): boolean {
    return this.bowTime >= 0;
  }

  /** Front flip for the double jump; replaces the air tuck for that jump. */
  flip(): void {
    this.flipTime = 0;
  }

  get flipping(): boolean {
    return this.flipTime >= 0;
  }

  update(dt: number, loco: Locomotion, windVelocity: THREE.Vector3): void {
    if (!this.loaded) return;
    // Sliding holds a still pose instead of pumping the run cycle.
    const v = loco.slide ? 0 : loco.speed;
    this.crouchW = damp(this.crouchW, loco.crouch ? 1 : 0, 10, dt);
    this.slideW = damp(this.slideW, loco.slide ? 1 : 0, loco.slide ? 16 : 7, dt);
    const runW = smoothstep(1.7, 3.1, v);
    const moveW = smoothstep(0.06, 0.75, v);
    const cycle = lerp(WALK_CYCLE_METERS, RUN_CYCLE_METERS, runW) * (1 + Math.max(0, v - 3.6) * 0.085);
    const previous = this.phase;
    const advance = (v * dt) / cycle;
    this.phase = (this.phase + (loco.grounded ? advance : advance * 0.3)) % 1;

    if (loco.grounded && v > 0.25) {
      const crossed = (target: number) => {
        const a = previous;
        const b = previous + (loco.grounded ? advance : advance * 0.3);
        return (a < target && b >= target) || (a < target + 1 && b >= target + 1);
      };
      const intensity = clamp(v / 5, 0.25, 1.2);
      if (crossed(CONTACT_L)) this.footsteps.push({ foot: 'L', intensity });
      if (crossed(CONTACT_R)) this.footsteps.push({ foot: 'R', intensity });
    }

    this.airBlend = damp(this.airBlend, loco.grounded ? 0 : 1, loco.grounded ? 14 : 8, dt);
    const ground = 1 - this.airBlend;
    this.walk.time = this.phase * this.walkDuration;
    this.run.time = ((this.phase + RUN_PHASE_OFFSET) % 1) * this.runDuration;
    this.idle.setEffectiveWeight((1 - moveW) * ground + 0.0001);
    this.walk.setEffectiveWeight(moveW * (1 - runW) * ground);
    this.run.setEffectiveWeight(moveW * runW * ground + this.airBlend);
    this.mixer.update(dt);
    this.sword.update(dt, { speed: loco.speed, flipping: this.flipping });
    this.updateFlip(dt, loco.grounded);

    const crouch = this.applyProcedural(dt, loco);

    // Landing compression spring.
    if (loco.grounded && !this.wasGrounded) this.landVel -= clamp(-loco.verticalVelocity, 0, 9) * 0.06;
    this.wasGrounded = loco.grounded;
    this.landVel += (-this.landDip * 90 - this.landVel * 12) * dt;
    this.landDip += this.landVel * dt;
    this.model.position.y = this.landDip - crouch;

    const targetLean = clamp(v * 0.016 + loco.sprint * 0.06, 0, 0.15) + (loco.grounded ? 0 : -0.04);
    const targetBank = clamp(-loco.turnRate * v * 0.022, -0.2, 0.2);
    this.lean = damp(this.lean, targetLean, 5, dt);
    this.bank = damp(this.bank, targetBank, 6, dt);
    // Lean and bank pivot at the feet; the flip turns about the hips and the spin about the body axis.
    qLean.setFromEuler(tmpEuler.set(this.lean, 0, this.bank));
    qTurn.setFromAxisAngle(axisY, this.sword.spinYaw).multiply(tmpQ.setFromAxisAngle(axisX, this.flipAngle));
    this.pivot.quaternion.copy(qTurn).multiply(qLean);
    this.pivot.position.copy(FLIP_CENTER).sub(tmpV.copy(FLIP_CENTER).applyQuaternion(qTurn));

    this.root.updateMatrixWorld(true);
    this.sword.apply();
    this.sword.measure(dt);
    this.updateCloth(dt, windVelocity);
  }

  private updateFlip(dt: number, grounded: boolean): void {
    if (this.flipTime < 0) {
      this.flipAngle = 0;
      this.flipTuck = damp(this.flipTuck, 0, 14, dt);
      return;
    }
    // Landing early finishes the turn quickly rather than planting mid-rotation.
    this.flipTime += dt * (grounded ? 3 : 1);
    const u = clamp(this.flipTime / FLIP_TIME, 0, 1);
    this.flipAngle = Math.PI * 2 * u * u * (3 - 2 * u);
    this.flipTuck = Math.max(this.flipTuck, Math.pow(Math.sin(Math.PI * Math.min(1, u * 1.15)), 0.6));
    if (u < 1) return;
    this.flipTime = -1;
    this.flipAngle = 0;
  }

  /** Returns how far the hips drop for the sword stance (metres). */
  private applyProcedural(dt: number, loco: Locomotion): number {
    const b = this.bones;
    const tuck = this.flipping ? 0 : this.airBlend * (1 - this.flipTuck);
    if (tuck > 0.001) {
      this.rotateBone(b.LeftUpLeg, -0.45 * tuck);
      this.rotateBone(b.RightUpLeg, -0.2 * tuck);
      this.rotateBone(b.LeftLeg, 0.7 * tuck);
      this.rotateBone(b.RightLeg, 0.9 * tuck);
      this.rotateBone(b.Spine1, 0.08 * tuck);
    }
    const flip = this.flipTuck;
    if (flip > 0.001) {
      // Tight tuck: knees to the chest, chin down.
      this.rotateBone(b.LeftUpLeg, -1.3 * flip);
      this.rotateBone(b.RightUpLeg, -1.15 * flip);
      this.rotateBone(b.LeftLeg, 1.95 * flip);
      this.rotateBone(b.RightLeg, 2.05 * flip);
      this.rotateBone(b.Spine, 0.28 * flip);
      this.rotateBone(b.Spine1, 0.24 * flip);
      this.rotateBone(b.Neck, 0.3 * flip);
      if (!this.sword.drawn) {
        tmpQ.setFromAxisAngle(axisY, 1.1 * flip);
        b.RightForeArm?.quaternion.multiply(tmpQ);
      }
      tmpQ.setFromAxisAngle(axisY, -1.1 * flip);
      b.LeftForeArm?.quaternion.multiply(tmpQ);
    }
    // Knees bend so the hips drop without the feet leaving the ground: sword stance or crouch.
    const s = this.slideW;
    const bendAngle = Math.max(0.3 * this.sword.crouch, 0.85 * this.crouchW) * (1 - s);
    let drop = 0;
    if (this.crouchW > 0.001) {
      this.rotateBone(b.Spine, 0.12 * this.crouchW * (1 - s));
      this.rotateBone(b.Spine1, 0.12 * this.crouchW * (1 - s));
    }
    if (s > 0.001) {
      // Baseball slide: lead leg out straight, trailing leg folded under, torso leaning back.
      this.rotateBone(b.RightUpLeg, -1.3 * s);
      this.rotateBone(b.RightLeg, 0.12 * s);
      this.rotateBone(b.RightFoot, 0.35 * s);
      this.rotateBone(b.LeftUpLeg, -0.3 * s);
      this.rotateBone(b.LeftLeg, 2.15 * s);
      this.rotateBone(b.Spine, -0.38 * s);
      this.rotateBone(b.Spine1, -0.25 * s);
      this.rotateBone(b.Neck, 0.4 * s);
      tmpQ.setFromAxisAngle(axisY, 0.35 * s);
      b.Spine?.quaternion.multiply(tmpQ);
      drop = 0.56 * s;
    }
    if (bendAngle > 0.001) {
      const a = bendAngle;
      this.rotateBone(b.LeftUpLeg, -a);
      this.rotateBone(b.RightUpLeg, -a);
      this.rotateBone(b.LeftLeg, 2 * a);
      this.rotateBone(b.RightLeg, 2 * a);
      this.rotateBone(b.LeftFoot, -a);
      this.rotateBone(b.RightFoot, -a);
      drop = Math.max(drop, 0.888 * (1 - Math.cos(a)));
    }
    if (this.bowTime >= 0) {
      this.bowTime += dt;
      const t = this.bowTime;
      const amount = smoothstep(0, 0.9, t) * (1 - smoothstep(2.1, 3.1, t));
      this.rotateBone(b.Spine, 0.22 * amount);
      this.rotateBone(b.Spine1, 0.2 * amount);
      this.rotateBone(b.Spine2, 0.16 * amount);
      this.rotateBone(b.Neck, 0.22 * amount);
      if (t > 3.2) this.bowTime = -1;
    }
    // A slow idle look around when standing in the wind.
    this.idleLook += dt;
    const still = 1 - smoothstep(0.1, 0.8, loco.speed);
    if (still > 0.01 && b.Neck) {
      tmpQ.setFromAxisAngle(axisY, Math.sin(this.idleLook * 0.23) * 0.25 * still * (this.sword.drawn ? 0.3 : 1));
      b.Neck.quaternion.multiply(tmpQ);
    }
    return drop;
  }

  private rotateBone(bone: THREE.Bone | undefined, angle: number): void {
    if (!bone) return;
    tmpQ.setFromAxisAngle(axisX, angle);
    bone.quaternion.multiply(tmpQ);
  }

  private updateCloth(dt: number, windVelocity: THREE.Vector3): void {
    for (const c of this.colliders) c.collider.center.copy(c.local).applyMatrix4(c.bone.matrixWorld);
    const colliders = this.colliders.map((c) => c.collider);
    sideRef.set(1, 0, 0).applyQuaternion(this.root.quaternion);
    for (const r of this.ribbons) {
      tmpV.copy(r.local).applyMatrix4(r.bone.matrixWorld);
      hangDown.copy(r.hang).applyQuaternion(this.root.quaternion);
      r.ribbon.update(dt, tmpV, hangDown, sideRef, windVelocity, colliders);
    }
  }

  resetCloth(): void {
    this.root.updateMatrixWorld(true);
    for (const r of this.ribbons) {
      tmpV.copy(r.local).applyMatrix4(r.bone.matrixWorld);
      hangDown.copy(r.hang).applyQuaternion(this.root.quaternion);
      r.ribbon.reset(tmpV, hangDown);
    }
  }

  bonePosition(name: string, target: THREE.Vector3): THREE.Vector3 {
    const bone = this.bones[name];
    return bone ? target.setFromMatrixPosition(bone.matrixWorld) : target.copy(this.root.position);
  }

  hatPosition(target: THREE.Vector3): THREE.Vector3 {
    return this.kasa ? this.kasa.getWorldPosition(target) : target.copy(this.root.position);
  }
}
